import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ComplianceStatusCard } from '../components/aml/ComplianceStatusCard';
import { Shield, Users, FileText, AlertTriangle, TrendingUp, Clock, Plus, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { amlAPI } from '../lib/amlAPI';
import { formatRelativeTime } from '../lib/fileUtils';

export default function AMLDashboard() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [metricsRes, alertsRes, activityRes] = await Promise.all([
        amlAPI.getDashboardMetrics(),
        amlAPI.getDashboardAlerts(),
        amlAPI.getDashboardActivity(10)
      ]);
      
      setMetrics(metricsRes.data);
      setAlerts(alertsRes.data.alerts || []);
      setActivity(activityRes.data.activity || []);
    } catch (error) {
      toast.error('Failed to load AML dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !metrics) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-32 bg-muted rounded"></div>
          <div className="grid grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const metricsConfig = [
    {
      title: 'Total Clients',
      value: metrics.total_clients,
      icon: Users,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'KYC Completed',
      value: metrics.clients_with_kyc,
      subtitle: `${metrics.kyc_completion_rate}% complete`,
      icon: FileText,
      color: 'bg-accent text-primary'
    },
    {
      title: 'High-Risk Clients',
      value: metrics.high_risk_clients,
      icon: AlertTriangle,
      color: 'bg-red-100 text-red-600'
    },
    {
      title: 'Pending Reviews',
      value: metrics.pending_reviews,
      icon: Clock,
      color: 'bg-amber-100 text-amber-600'
    }
  ];

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-foreground" />
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="aml-dashboard-title">
            AML/CTF Compliance
          </h1>
        </div>
        <p className="text-muted-foreground mt-1">
          AUSTRAC Tranche 2 compliance monitoring and management
        </p>
      </div>

      {/* Compliance Status Card */}
      <ComplianceStatusCard 
        status={metrics.compliance_status}
        percentage={metrics.compliance_percentage}
      />

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricsConfig.map((metric, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow duration-200" data-testid={`aml-metric-card-${index}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{metric.title}</p>
                  <p className="text-3xl font-bold text-foreground mt-2">{metric.value}</p>
                  {metric.subtitle && (
                    <p className="text-xs text-muted-foreground mt-2">{metric.subtitle}</p>
                  )}
                </div>
                <div className={`w-12 h-12 ${metric.color} rounded-lg flex items-center justify-center`}>
                  <metric.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              onClick={() => navigate('/aml/risk-assessment')}
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-accent"
              data-testid="quick-action-risk-assessment"
            >
              <Shield className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm font-medium">Risk Assessment</span>
            </Button>
            <Button 
              onClick={() => navigate('/aml/kyc')}
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-accent"
              data-testid="quick-action-kyc"
            >
              <FileText className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm font-medium">KYC Records</span>
            </Button>
            <Button 
              onClick={() => navigate('/aml/audit-logs')}
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-accent"
              data-testid="quick-action-audit-logs"
            >
              <Clock className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm font-medium">Audit Logs</span>
            </Button>
            <Button 
              onClick={() => navigate('/clients')}
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-accent"
              data-testid="quick-action-view-clients"
            >
              <Users className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm font-medium">View Clients</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alerts & Recent Activity - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Alerts & Reminders</CardTitle>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p>No alerts - all up to date!</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {alerts.map((alert, index) => (
                  <div 
                    key={index} 
                    className={`p-3 border-l-4 rounded-lg ${
                      alert.severity === 'critical' ? 'bg-red-50 dark:bg-red-950/30 border-red-500' :
                      alert.severity === 'warning' ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-500' :
                      'bg-blue-50 dark:bg-blue-950/30 border-blue-500'
                    }`}
                    data-testid={`aml-alert-${index}`}
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        alert.severity === 'critical' ? 'text-red-600' :
                        alert.severity === 'warning' ? 'text-amber-600' :
                        'text-blue-600'
                      }`} />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{alert.message}</p>
                        {alert.due_date && (
                          <p className="text-xs text-muted-foreground mt-1">Due: {new Date(alert.due_date).toLocaleDateString('en-AU')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {activity.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No recent activity</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[300px] overflow-y-auto">
                {activity.map((item) => (
                  <div key={item.id} className="flex items-start gap-4 pb-4 border-b border-border/60 last:border-0 last:pb-0" data-testid={`aml-activity-${item.id}`}>
                    <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm">{item.event_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      <p className="text-sm text-muted-foreground">{item.actor_name}</p>
                      {item.client_name && (
                        <p className="text-xs text-muted-foreground mt-1">Client: {item.client_name}</p>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground flex-shrink-0">{formatRelativeTime(item.timestamp)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}